import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/authStore';
import apiClient from '../../lib/api/client';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { KartiSay } from '../../components/brand/KartiCoach';
import { useKartiMessage } from '../../lib/karti';

const loginSchema = z.object({
  nickname: z.string().min(3, 'Nickname muss mindestens 3 Zeichen lang sein'),
  password: z.string().min(8, 'Passwort muss mindestens 8 Zeichen lang sein'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const formValid = loginSchema.safeParse(watch()).success;
  const idle = useKartiMessage('login');
  const kartiSays = error
    ? { mood: 'sad' as const, text: 'Das hat nicht geklappt. Prüf die Angaben und versuch es nochmal.' }
    : formValid
      ? { mood: 'happy' as const, text: 'Passt. Jetzt abschicken.' }
      : idle;

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/auth/login', data);
      const { user, accessToken } = response.data;

      setAuth(user, accessToken);
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-5">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="mx-auto mb-5 flex h-16 w-16 -rotate-3 items-center justify-center rounded-[20px] border-2.5 border-ink bg-marker shadow-hard-4">
              <span className="text-3xl font-extrabold">V</span>
            </div>
          </Link>
          <h1 className="mb-2 text-4xl font-extrabold tracking-tight">
            Willkommen zurück
          </h1>
          <p className="text-body">
            Melde dich an, um fortzufahren
          </p>
        </div>

        <KartiSay mood={kartiSays.mood} text={kartiSays.text} size={72} className="mb-5" />

        {/* Login Card */}
        <div className="overflow-hidden rounded-panel border-[3px] border-ink bg-cream shadow-hard-6">
          <div className="p-6 sm:p-7">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="nickname" className="label-mono !text-ink">
                  Nickname
                </label>
                <Input
                  id="nickname"
                  type="text"
                  placeholder="nickname"
                 
                  {...register('nickname')}
                />
                {errors.nickname && (
                  <p className="text-sm font-bold text-wrong-text">{errors.nickname.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="label-mono !text-ink">
                  Passwort
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                 
                  {...register('password')}
                />
                {errors.password && (
                  <p className="text-sm font-bold text-wrong-text">{errors.password.message}</p>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-3 rounded-tile border-2.5 border-ink bg-cream p-3.5 text-sm font-bold">
                  <span className="h-3 w-3 shrink-0 rounded-full border-2 border-ink bg-wrong" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 border-2 border-paper border-t-transparent rounded-full animate-spin"></div>
                    <span>Wird angemeldet...</span>
                  </div>
                ) : (
                  'Anmelden'
                )}
              </Button>
            </form>
          </div>

          <div className="border-t-2.5 border-ink bg-paper px-6 py-5">
            <p className="text-center text-sm text-body">
              Noch kein Konto?{' '}
              <span className="font-bold text-ink">
                Lade dir die VOC7-App herunter
              </span>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="label-mono inline-flex h-11 items-center !text-ink hover:underline"
          >
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}
